// src/pages/auth/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import custom components and hooks
import { TextField, Button, LoadingSpinner } from '../../design-system';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';
import useUIStore from '../../stores/uiStore';

// Global variable for token verification to prevent loops
window._tokenVerificationInProgress = window._tokenVerificationInProgress || false;
window._tokenVerificationComplete = window._tokenVerificationComplete || false;
window._tokenVerificationResult = window._tokenVerificationResult || null;

// Reset password page component
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from query parameters
  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get('token');
  
  const { resetPassword, verifyResetToken, isLoading, error, clearError } = useAuthStore();
  const { showSuccess } = useNotificationStore();
  const { addNotification } = useUIStore();
  
  // States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isTokenChecking, setIsTokenChecking] = useState(true);
  const [isResetComplete, setIsResetComplete] = useState(false);
  
  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
  
  // Toggle confirm password visibility
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  // Validation schema using Yup
  const validationSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
      ),
    confirmPassword: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  });
  
  // Formik setup
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await resetPassword(resetToken, values.password, values.confirmPassword);
        setIsResetComplete(true);
        showSuccess('Password reset successful! You can now sign in with your new password.');
      } catch (err) {
        addNotification({
          type: 'error',
          title: 'Reset Failed',
          message: err.message || 'Failed to reset password. Please try again.'
        });
      }
    },
  });
  
  // Check token only on initial mount
  useEffect(() => {
    const verifyToken = async () => {
      // Skip if no token
      if (!resetToken) {
        setIsTokenChecking(false);
        setIsTokenValid(false);
        addNotification({
          type: 'warning',
          title: 'Token Missing',
          message: 'Reset token is missing'
        });
        return;
      }
      
      // Check if verification is already complete (using window variable to prevent loops)
      if (window._tokenVerificationComplete) {
        setIsTokenValid(window._tokenVerificationResult);
        setIsTokenChecking(false);
        return;
      }
      
      // Prevent multiple simultaneous verifications
      if (window._tokenVerificationInProgress) {
        return;
      }
      
      try {
        window._tokenVerificationInProgress = true;
        
        // Call API to verify token
        await verifyResetToken(resetToken);
        
        // Update state and cache result
        window._tokenVerificationComplete = true;
        window._tokenVerificationResult = true;
        setIsTokenValid(true);
        setIsTokenChecking(false);
      } catch (err) {
        // Update state and cache result
        window._tokenVerificationComplete = true;
        window._tokenVerificationResult = false;
        setIsTokenValid(false);
        addNotification({
          type: 'warning',
          title: 'Invalid Token',
          message: 'Invalid or expired reset token. Please request a new reset link.'
        });
        setIsTokenChecking(false);
      } finally {
        window._tokenVerificationInProgress = false;
      }
    };
    
    // Call the verification function
    verifyToken();
    
    // Cleanup on unmount
    return () => {
      clearError();
    };
  }, []); // Empty dependency array - only run once
  
  // Show loading while checking token
  if (isTokenChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <LoadingSpinner size="medium" label="Verifying reset token..." />
      </Box>
    );
  }
  
  // Show error if token is invalid
  if (!isTokenValid && !isTokenChecking) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography component="h1" variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Password Reset
        </Typography>
        
        <Alert severity="error" sx={{ mb: 3 }}>
          Invalid or expired reset token. Please request a new password reset link.
        </Alert>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/forgot-password"
            variant="contained"
            magical
          >
            Request New Reset Link
          </Button>
        </Box>
      </Box>
    );
  }
  
  // Show success message after password reset
  if (isResetComplete) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Typography component="h1" variant="h5" gutterBottom>
            Password Reset Successful
          </Typography>
        </Box>
        
        <Alert severity="success" sx={{ mb: 3 }}>
          Your password has been reset successfully.
        </Alert>
        
        <Typography variant="body1" paragraph textAlign="center">
          You can now sign in with your new password.
        </Typography>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            magical
          >
            Sign In
          </Button>
        </Box>
      </Box>
    );
  }
  
  // Show reset password form
  return (
    <Box sx={{ width: '100%' }}>
      <Typography component="h1" variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
        Reset Your Password
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mb: 3 }}>
        Please enter your new password below
      </Typography>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Reset password form */}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="password"
          name="password"
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          magical
          margin="normal"
          autoComplete="new-password"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm New Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          magical
          margin="normal"
          autoComplete="new-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={handleToggleConfirmPassword}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          magical={true}
          loading={isLoading}
          sx={{ mt: 3, mb: 2, py: 1.5 }}
        >
          Reset Password
        </Button>
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            underline="hover"
            sx={{ 
              color: 'primary.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'primary.dark',
                transform: 'translateY(-1px)',
              }
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </form>
    </Box>
  );
};

export default ResetPassword;